import de from "./i18n/de.json"
import es from "./i18n/es.json"
import fi from "./i18n/fi.json"
import fr from "./i18n/fr.json"
import it from "./i18n/it.json"
import nb from "./i18n/nb.json"
import sv from "./i18n/sv.json"

/**
 * Hub's texts in Johku's languages. The English text is the key, as in the backend and the POS:
 * translate("fi", "Messages") gives "Viestit". The dictionaries come from Johku's Loco project
 * (texts tagged hub, `yarn loco pull`); a text missing from one shows in English. Plain functions,
 * so Hub's server can use them for emails and pushes too (lib/i18n.ts is the app's reactive side).
 */
export const LOCALES = ["en", "fi", "sv", "de", "nb", "it", "es", "fr"] as const
export type Locale = (typeof LOCALES)[number]

export const dictionaries: Record<Exclude<Locale, "en">, Record<string, string>> = { fi, sv, de, nb, it, es, fr }

/** Each language by its own name, as language pickers list them */
export const LANGUAGE_NAMES: Record<Locale, string> = {
  en: "English",
  fi: "Suomi",
  sv: "Svenska",
  de: "Deutsch",
  nb: "Norsk",
  it: "Italiano",
  es: "Español",
  fr: "Français",
}

/** "fi_FI", "fi-FI", "fi" or "no" (Norwegian); anything Hub doesn't speak is English. */
export function normalizeLocale(value: string | null | undefined): Locale {
  const code = String(value ?? "").slice(0, 2).toLowerCase()
  // Norwegian: Bokmål, as Johku has it
  if (code === "no" || code === "nn") return "nb"
  return (LOCALES as readonly string[]).includes(code) ? (code as Locale) : "en"
}

/** A text in a language; {name} in it is replaced from `vars`. */
export function translate(locale: Locale, text: string, vars?: Record<string, string | number>): string {
  const out = locale === "en" ? text : (dictionaries[locale][text] || text)
  return vars ? out.replace(/\{(\w+)\}/g, (match, key: string) => (key in vars ? String(vars[key]) : match)) : out
}

/** A t() for one language, e.g. for an email in the recipient's language. */
export function translator(locale: Locale) {
  return (text: string, vars?: Record<string, string | number>) => translate(locale, text, vars)
}

/** For dates and times: Finnish and Swedish as in Finland, English with a 24-hour clock. */
export function dateLocaleOf(locale: Locale): string {
  const tags: Record<Locale, string> = { en: "en-GB", fi: "fi-FI", sv: "sv-FI", de: "de-DE", nb: "nb-NO", it: "it-IT", es: "es-ES", fr: "fr-FR" }
  return tags[locale]
}
